<script setup lang="ts">
import { faArrowRight, faTimes } from '@fortawesome/pro-regular-svg-icons';
import type { PaletteColorProp } from '#imports';

interface IProps {
    title?: string;
    hasBack?: boolean;
    hideClose?: boolean;
    hideHeader?: boolean;
    externalFooter?: boolean;
    mobilePage?: boolean;
    showOverflow?: boolean;
    disableAnimation?: boolean;
    mobilePageHeaderColor?: PaletteColorProp | string;
    mobileHeaderTextColor?: PaletteColorProp | string;
    variant?: 'modal' | 'bottom-sheet';
    width?: string | number;
}
const {
    title = '',
    hasBack,
    mobilePage,
    showOverflow,
    hideHeader,
    hideClose,
    externalFooter,
    mobilePageHeaderColor = '',
    mobileHeaderTextColor = '',
    variant = 'modal',
    width,
} = defineProps<IProps>();
const emit = defineEmits<{
    close: [];
    'click:back': [event: MouseEvent];
    bottomSheetInternalPropSet: [
        properties: { title: string; footerHeight: number },
    ];
}>();
const { isMobile } = useSize();

const modalWidth = computed(() => width || (isMobile.value ? '90vw' : '40vw'));
const slots = useSlots();
const header = ref<HTMLElement>();
const footer = ref<HTMLElement>();
const footerHeight = ref(0);
const headerHeight = ref(0);
const hasFooter = computed(() => !!slots.footer);
const hasAside = computed(() => !!slots.aside);
const hasHeader = computed(() => !!slots.header);
onMounted(() => {
    if (!externalFooter) {
        footerHeight.value = footer.value?.getBoundingClientRect().height || 0;
    }
    headerHeight.value = header.value?.getBoundingClientRect().height || 0;
    if (variant === 'bottom-sheet') {
        emit('bottomSheetInternalPropSet', {
            title,
            footerHeight: footerHeight.value,
        });
    }
});
</script>
<template>
    <div
        class="mr-modal-root"
        :class="{
            'mobile-page': mobilePage,
            'show-overflow': showOverflow,
        }"
    >
        <div
            v-if="variant === 'modal'"
            class="root-container"
            :style="{
                '--padding-bottom': `${footerHeight}px`,
                '--width':
                    typeof modalWidth === 'string'
                        ? modalWidth
                        : `${modalWidth}px`,
            }"
        >
            <header v-if="!hideHeader">
                <slot name="extra-header" />
                <slot name="header">
                    <div ref="header" class="header">
                        <div
                            class="header-content"
                            :class="{ transparent: hasAside }"
                            :style="{
                                '--background': mobilePageHeaderColor,
                                '--color': mobileHeaderTextColor,
                            }"
                        >
                            <mr-button
                                v-if="hasBack"
                                variant="transparent"
                                :icon-button="faArrowRight"
                                :size="{ desktop: 'big', mobile: 'medium' }"
                                @click="emit('click:back', $event)"
                            />
                            <div v-else class="fake" />
                            <span>{{ hasAside ? '' : title }}</span>
                            <mr-button
                                v-if="!hideClose"
                                variant="transparent"
                                :icon-button="faTimes"
                                :size="{ desktop: 'big', mobile: 'medium' }"
                                @click="emit('close')"
                            />
                            <div v-else class="fake" />
                        </div>
                    </div>
                </slot>
            </header>
            <div class="content">
                <div
                    class="main-wrapper"
                    :class="{ 'has-aside': hasAside, separator: !hasHeader }"
                >
                    <div
                        v-if="headerHeight"
                        :style="{
                            minHeight: `${headerHeight}px`,
                            backgroundColor: hasAside ? 'white' : undefined,
                        }"
                        class="title"
                    >
                        {{ hasAside ? title : '' }}
                    </div>
                    <div
                        class="main-slot-wrapper"
                        :style="{
                            '--extra-height': `${
                                headerHeight + footerHeight
                            }px`,
                        }"
                    >
                        <slot />
                    </div>
                </div>
                <slot name="aside" />
            </div>
            <footer v-if="hasFooter" ref="footer" class="footer">
                <slot name="footer" />
            </footer>
        </div>
        <template v-else>
            <slot />
            <div v-if="hasFooter" ref="footer" class="bottom-sheet_footer">
                <slot name="footer" />
            </div>
        </template>
    </div>
</template>
<style lang="scss" scoped>
.mr-modal-root {
    max-height: 80vh;
    border-radius: 12px;
    overflow: hidden;
    @include mobile-only {
        .root-container {
            .header {
                .header-content {
                    background-color: var(--background) !important;
                    span {
                        color: var(--color) !important;
                    }
                }
            }
        }
        &.mobile-page {
            width: 100vw;
            min-height: 100vh;
            border-radius: 0;
            :global(.vfm__content) {
                border-radius: 0 !important;
                height: 100%;
            }
            .root-container {
                width: 100vw;
                min-height: 100vh;
                .header {
                    background-color: usePalette();
                    :deep() {
                        * {
                            color: white;
                        }
                    }
                    span {
                        @include tg-l($w7, white);
                        flex-grow: 1;
                        text-align: center;
                    }
                }
                .content {
                    flex: 1;
                    .main-wrapper {
                        width: 100%;
                        .main-slot-wrapper {
                            max-height: calc(100vh - var(--extra-height));
                        }
                    }
                }
            }
        }
    }
    .root-container {
        direction: rtl;
        display: flex;
        flex-direction: column;
        width: min(var(--width), 100vw);
        border-radius: 12px;
        .header {
            position: fixed;
            top: 0;
            right: 0;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            z-index: 10;
            .header-content {
                display: flex;
                align-items: center;
                padding: 12px;
                gap: 12px;
                background-color: white;
                border-radius: 12px !important;
                &.transparent {
                    background-color: transparent;
                }
                span {
                    color: usePalette($p-cool-gray, $p-900);
                }
            }
            &.with-shadow {
                @include shadow();
            }
            .fake {
                width: 48px;
                flex-shrink: 0;
            }
            span {
                @include tg-xl($w7);
                flex-grow: 1;
                text-align: center;
                white-space: nowrap;
            }
        }
        .content {
            display: flex;
            .main-wrapper {
                display: flex;
                flex-direction: column;
                &.separator {
                    @include shadow(2);
                }
                width: 100%;
                &.has-aside {
                    @include desktop-only {
                        width: fit-content;
                    }
                }
                padding-bottom: var(--padding-bottom);
                .title {
                    @include tg-xl($w7);
                    text-align: center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .main-slot-wrapper {
                    background-color: white;
                    height: 100%;
                    padding: 12px;
                    overflow: auto;
                    max-height: calc(80vh - var(--extra-height));
                    &::-webkit-scrollbar {
                        width: 5px;
                    }
                    &::-webkit-scrollbar-thumb {
                        width: 5px;
                        border-radius: 5px;
                        background-color: usePalette();
                    }
                    &::-webkit-scrollbar-track {
                        width: 5px;
                        background-color: usePalette($p-blue, $p-100);
                    }
                }
            }
        }
        .footer {
            width: 100%;
            padding: 20px;
            position: fixed;
            bottom: 0;
            right: 0;
            background-color: white;
            border-bottom-right-radius: 12px;
            border-bottom-left-radius: 12px;
            @include mobile-only {
                padding: 12px 20px;
            }
            &.with-shadow {
                @include shadow();
            }
        }
    }
}
:deep() {
    .pop-enter-active,
    .pop-enter-to {
        animation: slide-in-bottom 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)
            both;
    }
    @keyframes slide-in-bottom {
        0% {
            transform: translateX(-50%) translateY(1000px);
            opacity: 0;
        }
        100% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
        }
    }
    .pop-leave-active,
    .pop-leave-to {
        animation: slide-out-bottom 0.2s cubic-bezier(0.55, 0.085, 0.68, 0.53)
            both;
    }
    @keyframes slide-out-bottom {
        0% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
        }
        100% {
            transform: translateX(-50%) translateY(1000px);
            opacity: 0;
        }
    }
}
</style>
